import React, { Fragment, useState, useRef, useEffect, memo, useMemo } from 'react';

import Modal from "core/ui-kit/modal";

import useDevice from "core/hooks/useDevice";
import { useTranslation } from "core/hooks/useTranslation";
import useDate from "core/hooks/useDate";

import showSuccess from "core/helpers/common/showSuccess";
import getTimeZoneOffset from "core/helpers/date/getTimeZoneOffset";

import accountService from "core/services/apiServices/accountService";

import { POPUP_SIZE } from "core/constants/common";
import timeZones from "core/systemData/timezones";

import ModalContent from './modalContent';
import MainButton from "./mainButton";

const TimeZones = ({ isCollapsed, closeSidebar }) => {

    const { t } = useTranslation();
    const { isMobile } = useDevice();

    const [opened, setOpened] = useState(false);
    const [saving, setSaving] = useState(false);

    const { dateService } = useDate();

    const initialTimeZoneId = dateService.getTimeZoneId();
    const [selectedTimeZoneId, setSelectedTimeZoneId] = useState(initialTimeZoneId || null);

    const selectedTimeZoneConfig = timeZones.find((config) => selectedTimeZoneId === config.id);
    const usesDaylightSaving = selectedTimeZoneConfig?.dstSupport;

    const initialDayLightSaving = dateService.getDaylightSaving();
    const [isDaylightChecked, setIsDaylightChecked] = useState(initialDayLightSaving);

    const notChanged = initialTimeZoneId === selectedTimeZoneId && initialDayLightSaving === isDaylightChecked;

    const timer = useRef(null);

    const [date, setDate] = useState(new Date());

    const resetState = () => {
        setSaving(false);
        setSelectedTimeZoneId(initialTimeZoneId || null);
        setIsDaylightChecked(initialDayLightSaving);
    }

    const getUntilNext = () => {
        const currentTime = dateService.getNow();
        let nextTime = dateService.getNow();
        nextTime = dateService.setUnit(nextTime, "seconds", 0);
        nextTime = dateService.setUnit(nextTime, "milliseconds", 0);
        nextTime = dateService.addUnit(nextTime, 1, "minutes", 1);
        return dateService.getDifference(nextTime, currentTime, "milliseconds");
    }

    const runClock = () => {
        const untilNext = getUntilNext();
        timer.current = setTimeout(() => {
            setDate(new Date());
            runClock();
        }, untilNext);
    }

    const timeZoneOptions = useMemo(() => {
        return (
            [{ value: null, text: t("backoffice.common.autoDetect") }].concat(
                timeZones.map(item => (
                    { value: item.id, text: item.description }
                ))
            )
        )
    }, [timeZones]);

    const selectTimeZone = () => {
        setSaving(true);
        const daylightSaving = usesDaylightSaving ? isDaylightChecked : false;

        const params = {
            timeZoneId: selectedTimeZoneId,
            daylightSaving: daylightSaving
        }
        accountService.setTimeZone(params)
            .then(({ value, message }) => {
                const timeZoneId = value.timeZoneId;
                let timeZoneOffset = getTimeZoneOffset(timeZoneId, timeZones);
                const timeZoneConfig = timeZones.find(config => config.id === timeZoneId);
                const dayLightRangeStart = timeZoneConfig?.dstStart;
                const dayLightRangeEnd = timeZoneConfig?.dstEnd;

                if (daylightSaving && dayLightRangeStart && dayLightRangeEnd) {
                    const nowUtc = new Date();
                    const isInDaylightRange = dateService.isInRange(nowUtc, timeZoneConfig.dstStart.$date, timeZoneConfig.dstEnd.$date);

                    if (isInDaylightRange) {
                        timeZoneOffset = timeZoneOffset + 1;
                    }
                }
                dateService.setTimeZone(timeZoneOffset);
                dateService.setTimeZoneId(timeZoneId);
                setSelectedTimeZoneId(timeZoneId);
                dateService.setDaylightSaving(daylightSaving);
                showSuccess(message);
            })
            .finally(() => {
                setSaving(false);
                setOpened(false);
            })
    }

    const onOpenTimeZonesControl = (evt) => {
        evt.stopPropagation();
        evt.preventDefault();
        if (isMobile) {
            closeSidebar();
        }
        setOpened(true);
    }

    useEffect(() => {
        if (isCollapsed) {
            clearTimeout(timer.current);
        } else {
            setDate(new Date());
            runClock();
        }

        return () => {
            clearTimeout(timer.current);
        }
    }, [isCollapsed]);

    useEffect(() => {
        if (!opened) {
            resetState();
        }
    }, [opened]);

    return (
        <Fragment>
            <MainButton
                onOpenTimeZonesControl={onOpenTimeZonesControl}
                isCollapsed={isCollapsed}
                opened={opened}
                date={date}
            />
            <Modal
                open={opened}
                title={t('backoffice.common.timezone')}
                size={POPUP_SIZE.MIDDLE}
                onCancel={() => setOpened(false)}
                cancelText={t('backoffice.common.cancel')}
                okText={t('backoffice.common.save')}
                okButtonProps={{ disabled: notChanged }}
                onOk={selectTimeZone}
            >
                <ModalContent
                    setSelectedTimeZoneId={setSelectedTimeZoneId}
                    setIsDaylightChecked={setIsDaylightChecked}
                    usesDaylightSaving={usesDaylightSaving}
                    selectedTimeZoneId={selectedTimeZoneId}
                    isDaylightChecked={isDaylightChecked}
                    timeZoneOptions={timeZoneOptions}
                    saving={saving}
                />
            </Modal>
        </Fragment>
    )
}

export default memo(TimeZones);
